import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Subcategory, subcategoriesService, SubcategoryTreeNode } from "../../../../services/subcategories";

type EditableSubcategory = Omit<Subcategory, "id" | "created_at" | "updated_at"> & {
  id?: string;
};

interface SubcategoriesDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editingSubcategory: EditableSubcategory | null;
  setEditingSubcategory: React.Dispatch<React.SetStateAction<EditableSubcategory | null>>;
  allSubcategories?: Subcategory[];
}

const SubcategoriesDialog: React.FC<SubcategoriesDialogProps> = ({
  open,
  onClose,
  onSave,
  editingSubcategory,
  setEditingSubcategory,
  allSubcategories = [],
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (open && !editingSubcategory) {
      setEditingSubcategory({
        category_key: "",
        key: "",
        label_en: "",
        label_ar: "",
        label_fr: "",
        parent_id: null,
        slug: "",
        meta_title: "",
        meta_description: "",
        display_order: 0,
        is_active: true,
      });
    }
  }, [open, editingSubcategory, setEditingSubcategory]);

  // Filter out the current subcategory and its descendants to prevent circular references
  const getAvailableParents = (): Subcategory[] => {
    const currentId = editingSubcategory?.id;
    if (!currentId || !allSubcategories.length) return allSubcategories;

    const tree = subcategoriesService.buildTree(allSubcategories);
    const descendantIds = new Set<string>();

    const collectDescendants = (nodes: SubcategoryTreeNode[]) => {
      for (const node of nodes) {
        descendantIds.add(node.id);
        collectDescendants(node.children);
      }
    };

    // Find the current node in the tree
    const findAndCollect = (nodes: SubcategoryTreeNode[]): boolean => {
      for (const node of nodes) {
        if (node.id === currentId) {
          collectDescendants(node.children);
          descendantIds.add(node.id);
          return true;
        }
        if (findAndCollect(node.children)) return true;
      }
      return false;
    };
    findAndCollect(tree);

    return allSubcategories.filter((s) => !descendantIds.has(s.id));
  };

  const handleSelectChange = (field: string) => (event: any) => {
    setEditingSubcategory((prev) =>
      prev ? { ...prev, [field]: event.target.value || null } : prev
    );
  };

  const slugify = (str: string) =>
    str.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[\s_]+/g, "-");

  const handleChange = (field: keyof EditableSubcategory) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const raw = event.target.value;
    const value =
      field === "display_order" ? parseInt(raw, 10) || 0 : raw;

    setEditingSubcategory((prev) => {
      if (!prev) return prev;
      const updated: EditableSubcategory = { ...prev, [field]: value };
      // Auto-generate key from label_en when creating new (no id) and key hasn't been manually changed
      if (field === "label_en" && !prev.id && prev.key === slugify(prev.label_en || "")) {
        updated.key = slugify(raw);
      }
      return updated;
    });
  };

  const handleSwitchChange = (field: "is_active") => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditingSubcategory((prev) =>
      prev
        ? {
            ...prev,
            [field]: event.target.checked,
          }
        : prev
    );
  };

  const handleSave = () => {
    if (!editingSubcategory?.category_key?.trim()) {
      alert(t("admin.contentManagement.fields.categoryKeyRequired", { defaultValue: "Category Key is required" }));
      return;
    }
    if (!editingSubcategory?.key?.trim()) {
      alert(t("admin.contentManagement.fields.keyRequired", { defaultValue: "Key is required. It will be auto-filled when you type the English label." }));
      return;
    }
    onSave();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {editingSubcategory?.id
          ? t("admin.contentManagement.editSubcategory", { defaultValue: "Edit Subcategory" })
          : t("admin.contentManagement.addSubcategory", { defaultValue: "Add Subcategory" })}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
          <TextField
            label={t("admin.contentManagement.fields.categoryKey", { defaultValue: "Category Key" })}
            value={editingSubcategory?.category_key || ""}
            onChange={handleChange("category_key")}
            fullWidth
            required
          />
          <TextField
            label={t("admin.contentManagement.fields.key")}
            value={editingSubcategory?.key || ""}
            onChange={handleChange("key")}
            fullWidth
            required
            helperText={t("admin.contentManagement.fields.keyHint", { defaultValue: "Unique slug, auto-filled from English label" })}
          />
          <TextField
            label={t("admin.contentManagement.fields.labelEn")}
            value={editingSubcategory?.label_en || ""}
            onChange={handleChange("label_en")}
            fullWidth
          />
          <TextField
            label={t("admin.contentManagement.fields.labelAr")}
            value={editingSubcategory?.label_ar || ""}
            onChange={handleChange("label_ar")}
            fullWidth
          />
          <TextField
            label={t("admin.contentManagement.fields.labelFr")}
            value={editingSubcategory?.label_fr || ""}
            onChange={handleChange("label_fr")}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>{t("admin.contentManagement.parentSubcategory", { defaultValue: "Parent Subcategory" })}</InputLabel>
            <Select
              value={editingSubcategory?.parent_id || ""}
              label={t("admin.contentManagement.parentSubcategory", { defaultValue: "Parent Subcategory" })}
              onChange={handleSelectChange("parent_id")}
            >
              <MenuItem value="">
                <em>{t("common.none", { defaultValue: "None (Top-level)" })}</em>
              </MenuItem>
              {subcategoriesService
                .flattenTree(
                  subcategoriesService.buildTree(
                    getAvailableParents().filter(
                      (s) =>
                        !editingSubcategory?.category_key ||
                        s.category_key === editingSubcategory.category_key,
                    ),
                  ),
                )
                .map((sub) => (
                <MenuItem key={sub.id} value={sub.id} sx={{ pl: 2 + sub.depth * 2 }}>
                  {sub.path} ({sub.category_key})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={t("admin.contentManagement.fields.displayOrder")}
            type="number"
            value={editingSubcategory?.display_order ?? 0}
            onChange={handleChange("display_order")}
            fullWidth
          />

          <Typography variant="subtitle2" sx={{ mt: 1, color: "text.secondary" }}>
            {t("admin.contentManagement.seoFields", { defaultValue: "SEO Metadata" })}
          </Typography>

          <TextField
            label={t("admin.contentManagement.fields.slug", { defaultValue: "URL Slug" })}
            value={editingSubcategory?.slug || ""}
            onChange={handleChange("slug")}
            fullWidth
            helperText={t("admin.contentManagement.slugHelper", { defaultValue: "Auto-generated from key if left empty" })}
          />
          <TextField
            label={t("admin.contentManagement.fields.metaTitle", { defaultValue: "Meta Title" })}
            value={editingSubcategory?.meta_title || ""}
            onChange={handleChange("meta_title")}
            fullWidth
          />
          <TextField
            label={t("admin.contentManagement.fields.metaDescription", { defaultValue: "Meta Description" })}
            value={editingSubcategory?.meta_description || ""}
            onChange={handleChange("meta_description")}
            fullWidth
            multiline
            rows={2}
          />

          <FormControlLabel
            control={
              <Switch
                checked={editingSubcategory?.is_active ?? true}
                onChange={handleSwitchChange("is_active")}
              />
            }
            label={t("admin.contentManagement.active")}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button onClick={handleSave} variant="contained">
          {t("common.save", { defaultValue: "Save" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubcategoriesDialog;